'use client';

import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type CSSProperties,
} from 'react';
import gsap from 'gsap';
import { runHudScramble } from 'utils/performanceLab/hudScramble';
import styles from './HudAnnotations.module.css';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

/* ─── Styled Components ─── */

export interface HudAnnotationWrapperProps
  extends ComponentPropsWithoutRef<'div'> {
  $left: string;
  $top: string;
}

export const HudAnnotationWrapper = forwardRef<
  HTMLDivElement,
  HudAnnotationWrapperProps
>(function HudAnnotationWrapper({ className, style, $left, $top, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={cx(styles.hudAnnotationWrapper, className)}
      style={{ ...style, '--hud-left': $left, '--hud-top': $top } as CSSProperties}
      {...rest}
    />
  );
});

const CornerDot = ({
  $top,
  $bottom,
  $left,
  $right,
}: {
  $top?: string;
  $bottom?: string;
  $left?: string;
  $right?: string;
}) => (
  <span
    className={styles.cornerDot}
    style={
      {
        ...($top ? { '--cd-top': $top } : {}),
        ...($bottom ? { '--cd-bottom': $bottom } : {}),
        ...($left ? { '--cd-left': $left } : {}),
        ...($right ? { '--cd-right': $right } : {}),
      } as CSSProperties
    }
  />
);

/* ─── Label Pool ─── */

const ALL_LABELS = [
  'BLOOD MARKERS',
  'BODY COMPOSITION ANALYSIS',
  'CARDIOVASCULAR HEALTH',
  'VITALS',
  'VO2 MAX',
  'PULMONARY FUNCTION TEST',
  'ORAL HEALTH',
  'AUDIOMETRY',
  'VISION',
  'GENETICS',
  'GUT MICROBIOME',
  'NUTRITION',
  'COGNITION',
  'METABOLIC RISK ASSESSMENT',
  'IV THERAPY',
  'SPORTS MASSAGE',
  'COMPRESSION THERAPY',
  'HYPERBARIC OXYGEN THERAPY',
  'CRYOTHERAPY',
  'COLD PLUNGE',
  'INFRARED SAUNA',
];

function pickRandom(count: number, exclude?: string[]): string[] {
  const pool = exclude
    ? ALL_LABELS.filter((l) => !exclude.includes(l))
    : [...ALL_LABELS];

  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  if (pool.length >= count) return pool.slice(0, count);

  const extra = ALL_LABELS.filter((l) => !pool.includes(l));
  for (let i = extra.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [extra[i], extra[j]] = [extra[j], extra[i]];
  }
  return [...pool, ...extra].slice(0, count);
}

/* ─── Cycling Hook ─── */

export function useHudCycling(frameCount: number, intervalMs = 12000) {
  const [activeLabels, setActiveLabels] = useState<string[]>(() =>
    pickRandom(frameCount),
  );
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveLabels((prev) => pickRandom(frameCount, prev));
      setCycle((c) => c + 1);
    }, intervalMs);
    return () => clearInterval(intervalId);
  }, [frameCount, intervalMs]);

  return { activeLabels, cycle };
}

/* ─── HudAnnotation Component ─── */

export const HudAnnotation = ({
  label,
  prefix,
  left,
  top,
  delay,
  cycle,
}: {
  label: string;
  prefix: string;
  left: string;
  top: string;
  delay: number;
  cycle: number;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const prefixRef = useRef<HTMLSpanElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const hasMounted = useRef(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const prefixEl = prefixRef.current;
    const labelEl = labelRef.current;
    if (!wrapper || !prefixEl || !labelEl) return;

    labelEl.textContent = '';

    if (!hasMounted.current) {
      hasMounted.current = true;
      const tl = gsap.timeline({ delay });
      tl.to(wrapper, { opacity: 1, duration: 0.3, ease: 'power2.out' });
      tl.to(
        prefixEl,
        { opacity: 1, duration: 0.25, ease: 'power2.out' },
        '-=0.1',
      );
      tl.call(() => runHudScramble(labelEl, label, 800));
      return () => {
        tl.kill();
      };
    }

    const timerId = setTimeout(() => {
      runHudScramble(labelEl, label, 800);
    }, delay * 1000);

    return () => clearTimeout(timerId);
  }, [cycle, label, delay]);

  return (
    <HudAnnotationWrapper ref={wrapperRef} $left={left} $top={top}>
      <span ref={prefixRef} className={styles.annotationPrefix}>
        {prefix}
      </span>
      <div className={styles.annotationFrame}>
        <CornerDot $top="-2px" $left="-2px" />
        <CornerDot $bottom="-2px" $left="-2px" />
        <CornerDot $top="-2px" $right="-2px" />
        <CornerDot $bottom="-2px" $right="-2px" />
        <span ref={labelRef} className={styles.annotationLabel} />
      </div>
    </HudAnnotationWrapper>
  );
};
